import React from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import LoginRedirect from './components/Login/LoginRedirect';
import DashboardContent from './components/Dashboard/DashboardContent';
import ProtectedRoute from './context/ProtectedRoute';
import EditUser from './components/User/EditUser';
import { ThemeProvider, createTheme } from "@mui/material";

// import { appInsights } from './AppInsights';
import ForgotPassword from './components/ForgotPassword/ForgotPassword';
import ForgotPasswordSuccess from './components/ForgotPassword/ForgotPasswordSuccess';
import ResetPassword from './components/ForgotPassword/ResetPassword';
import ResetPasswordSuccess from './components/ForgotPassword/ResetPasswordSuccess';
import PageNotFound from './components/PageNotFound';
import NewPassword from './components/NewPassword/NewPassword';
import NewPasswordSuccess from './components/NewPassword/NewPasswordSuccess';
import BrandList from './components/Brands/BrandList';
import GroupList from './components/Groups/GroupList';
import ApiList from './components/API/ApiList';
import Template from './components/Templates/TemplateList';
import AddTemplateForm from './components/Templates/AddTemplateForm';
import EditTemplateForm from './components/Templates/EditTemplateForm';
import NewCampaing from './components/Campaigns/NewCampaing';
import CampaignList from './components/Campaigns/CampaignList';
import EditCampaing from './components/Campaigns/EditCampaing';
import AddTriggerForm from './components/Triggers/AddTriggerForm';
import EditTriggerForm from './components/Triggers/EditTriggerForm';
import AddClient from './components/Clients/AddClient';
import AddEmailAPIClient from './components/EmailAPI/Clients/AddClient';
import EditEmailAPIClient from './components/EmailAPI/Clients/EditClient';

import ESPSettings from './components/EmailAPI/ESPConnection/ESPList';
import AddESPSetting from './components/EmailAPI/ESPConnection/AddESP';
import EditESPAPI from './components/EmailAPI/ESPConnection/EditESP';
// import ViewTriggerReports from './components/Triggers/ViewTriggerReports';
import ViewCampaignReports from './components/Campaigns/ViewCampaignReports';
import CampaignSummary from './components/Campaigns/CampaignSummary';
import BillingReport from './components/Reports/BillingReports';
import TestEmailsendReport from './components/Reports/TestEmailsendReports';
import BounceReport from './components/Reports/BounceReport';
import SeedList from './components/SeedList/SeedList';
import SuppressionList from './components/SuppressionList/SuppressionList';
import NewBulkCampaign from './components/BulkEmail/NewBulkCampaign';
import EditBulkEmailCampaign from './components/BulkEmail/EditBulkEmailCampaign';
import GanttSchedulePage from './components/BulkEmail/GanttChart/GanttSchedulePage';
import AppLayout from './AppLayout';
import WebhookList from './components/Webhooks/WebhookList';
import UserList from './components/User/UserList';
import RoleList from './components/Roles/RoleList';
import ClientList from './components/Clients/ClientList';
import AppConfigDetailsPage from './components/AppConfig/AppConfigDetailsPage';
import TriggerList from './components/Triggers/TriggerList';
import EmailAPIClientList from './components/EmailAPI/Clients/ClientList';
import BulkEmailList from './components/BulkEmail/BulkEmailList';
import UnsubscribeList from './components/Unsubscribe/UnsubscribeList';
import SMSCampaignTemplate from './components/SMSCampaign/SMSCampaignTemplate';
import EditClient from './components/Clients/EditClient';
import ReSubscribeList from './components/Resubscribe/ResubscribeList';
import PLDReport from './components/Reports/PLDReports';


const theme = createTheme({
  palette: {
    primary: {
      main: getComputedStyle(document.documentElement).getPropertyValue("--primary-color").trim() !== ""
        ? getComputedStyle(document.documentElement).getPropertyValue("--primary-color").trim()
        : "#d6242d",
    },
    text: {
      primary: getComputedStyle(document.documentElement).getPropertyValue("--font-color").trim() !== ""
        ? getComputedStyle(document.documentElement).getPropertyValue("--font-color").trim()
        : "#3A3A3A",
    },
    divider: getComputedStyle(document.documentElement).getPropertyValue("--border-color").trim() !== ""
      ? getComputedStyle(document.documentElement).getPropertyValue("--border-color").trim()
      : "#E6ECF2",
  },
  typography: {
    fontFamily: "Manrope, 'sans-serif'",
    fontSize: isNaN(parseInt(getComputedStyle(document.documentElement).getPropertyValue("--primary-font-size")))
      ? 14
      : parseInt(getComputedStyle(document.documentElement).getPropertyValue("--primary-font-size")),
    allVariants: {
      color: getComputedStyle(document.documentElement).getPropertyValue("--font-color").trim() !== ""
        ? getComputedStyle(document.documentElement).getPropertyValue("--font-color").trim()
        : "#3A3A3A",
    },
  },
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: 'var(--placeholder-color, #77787B)',
          "& fieldset": {
            borderColor: getComputedStyle(document.documentElement).getPropertyValue("--border-color").trim() !== ""
              ? getComputedStyle(document.documentElement).getPropertyValue("--border-color").trim()
              : "#E6ECF2",
          },
          "&:hover fieldset": {
            borderColor: getComputedStyle(document.documentElement).getPropertyValue("--border-color").trim() !== ""
              ? getComputedStyle(document.documentElement).getPropertyValue("--border-color").trim()
              : "#E6ECF2",
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: getComputedStyle(document.documentElement).getPropertyValue("--border-color").trim() !== ""
              ? getComputedStyle(document.documentElement).getPropertyValue("--border-color").trim()
              : "#E6ECF2",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: getComputedStyle(document.documentElement).getPropertyValue("--border-color").trim() !== ""
              ? getComputedStyle(document.documentElement).getPropertyValue("--border-color").trim()
              : "#E6ECF2",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: getComputedStyle(document.documentElement).getPropertyValue("--border-color").trim() !== ""
              ? getComputedStyle(document.documentElement).getPropertyValue("--border-color").trim()
              : "#E6ECF2",
            borderWidth: '1px'
          },
          "& .MuiOutlinedInput-notchedOutline legend": {
            color: getComputedStyle(document.documentElement).getPropertyValue("--border-color").trim() !== ""
              ? getComputedStyle(document.documentElement).getPropertyValue("--border-color").trim()
              : "#E6ECF2",
          },
        },
      },
    },
  },
})

const AppRoutes: React.FC = () => {
  // const location = useLocation();

  // useEffect(() => {
  //   appInsights.trackPageView({ name: location.pathname });
  // }, [location]);

  return (
    <Routes>
      <Route path="/" element={<LoginRedirect />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/forgot-password-success" element={<ForgotPasswordSuccess />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/reset-password-success" element={<ResetPasswordSuccess />} />
      <Route path="/create-password" element={<NewPassword />} />
      <Route path="/create-password-success" element={<NewPasswordSuccess />} />
      <Route path="/404" element={<PageNotFound />} />
      <Route path="*" element={<PageNotFound />} />

      {/* 
  Protected routes: All routes below use AppLayout and require user authentication 
  Instead of wrapping MainLayout around each component, we define a parent route with AppLayout as its element.
*/}
      <Route element={<ProtectedRoute><AppLayout /> </ProtectedRoute>}>
        <Route path="/dashboard" element={<DashboardContent />} />
        <Route path="/template" element={<Template />} />
        <Route path="/campaigns" element={<CampaignList />} />
        <Route path="/template/new-template" element={<AddTemplateForm />} />
        <Route path="/template/edit-template/edit/:id" element={<EditTemplateForm />} />
        <Route path="/brandsandgroups/manage-brands" element={<BrandList />} />
        <Route path="/brandsandgroups/manage-groups" element={<GroupList />} />
        <Route path="/apiandwebhooks/manage-api" element={<ApiList />} />
        <Route path="/apiandwebhooks/manage-webhooks" element={<WebhookList />} />
        <Route path="/admin/manage-users" element={<UserList />} />
        <Route path="/admin/manage-roles" element={<RoleList />} />
        <Route path="/admin/manage-users/edit/:id" element={<EditUser />} />
        <Route path="/admin/manage-clients" element={<ClientList />} />
        <Route path="/admin/manage-clients/add-client/:id?" element={<AddClient />} />
        <Route path="/admin/manage-clients/edit/:id" element={<EditClient />} />
        <Route path="/admin/manage-appconfig" element={<AppConfigDetailsPage />} />
        <Route path="/campaigns/new-campaign" element={<NewCampaing />} />
        <Route path="/campaigns/summary/:type/:id" element={<CampaignSummary />} />
        <Route path="/campaigns/edit-campaign/:id" element={<EditCampaing />} />
        <Route path="/campaigns/view-report/:id" element={<ViewCampaignReports />} />
        <Route path="/bulk-email/view-report/:id" element={<ViewCampaignReports />} />
        <Route path="/trigger" element={<TriggerList />} />
        <Route path="/trigger/new-trigger" element={<AddTriggerForm />} />
        <Route path="/trigger/edit-trigger/:id" element={<EditTriggerForm />} />
        <Route path="/trigger/view-report/:id" element={<ViewCampaignReports />} />
        <Route path="/email-api/manage-clients" element={<EmailAPIClientList />} />
        <Route path="/email-api/manage-clients/add-client" element={<AddEmailAPIClient />} />
        <Route path="/email-api/manage-clients/edit/:id" element={<EditEmailAPIClient />} />
        <Route path="/email-api" element={<ESPSettings />} />
        <Route path="/email-api/esp-settings/add-espsettings" element={<AddESPSetting />} />
        <Route path="/email-api/esp-settings/edit/:id" element={<EditESPAPI />} />
        <Route path="/reports/billing-report" element={<BillingReport />} />
        <Route path="/reports/testemailsend-report" element={<TestEmailsendReport />} />
        <Route path="/reports/bounce-report" element={<BounceReport />} />
        <Route path="/seed-list" element={<SeedList />} />
        <Route path="/suppression-list" element={<SuppressionList />} />
        <Route path="/bulk-email" element={<BulkEmailList />} />
        <Route path="/bulk-email/new-bulk-campaign" element={<NewBulkCampaign />} />
        <Route path="/bulk-email/edit-bulk-email/:id" element={<EditBulkEmailCampaign />} />
        <Route path="/reports/campaign-schedule" element={<GanttSchedulePage />} />
        <Route path="/unsubscribe-list" element={<UnsubscribeList />} />
        <Route path="/resubscribe-list" element={<ReSubscribeList />} />
        <Route path="/sms-campaign" element={<SMSCampaignTemplate />} />
        <Route path="/reports/pld-report" element={<PLDReport />} />
      </Route>



    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <ThemeProvider theme={theme} >
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </ThemeProvider>
    </Router>
  );
};

export default App;
